import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { LOGO_URL, fmtDate, openPodFile, API_BASE } from "@/lib/api";
import { Printer, ArrowLeft, FileCheck2 } from "lucide-react";

export default function BillPrint() {
  const { id } = useParams();
  const [b, setB] = useState(null);
  const [pods, setPods] = useState([]);
  const [sig, setSig] = useState(null);
  useEffect(() => { api.get(`/bills/${id}`).then(({ data }) => setB(data)); }, [id]);
  useEffect(() => { api.get(`/bills/${id}/signatory`).then(({ data }) => setSig(data)).catch(() => {}); }, [id]);

  // Find associated CNs that have a linked POD, so we can offer to print them with the bill.
  useEffect(() => {
    if (!b) return;
    api.get("/consignments").then(({ data }) => {
      const cns = new Set((b.items || []).map((it) => it.cn_no).filter(Boolean));
      setPods(data.filter((c) => cns.has(c.cn_no) && c.pod_filename));
    }).catch(() => {});
  }, [b]);

  // Inject portrait @page CSS while this page is mounted (unmount removes it)
  useEffect(() => {
    const s = document.createElement("style");
    s.setAttribute("data-bill-print", "1");
    s.textContent = `@media print { @page { size: 210mm 297mm !important; margin: 0 !important; } }`;
    document.head.appendChild(s);
    return () => { s.remove(); };
  }, []);

  if (!b) return <div className="p-8 text-slate-500">Loading…</div>;

  return (
    <div className="p-6 bg-slate-100 min-h-screen" data-testid="bill-print-page">
      <div className="no-print flex items-center justify-between mb-4 max-w-[210mm] mx-auto">
        <Link to="/portal/bills" className="text-slate-600 hover:text-slate-900 text-sm inline-flex items-center gap-2">
          <ArrowLeft size={16}/> Back
        </Link>
        <button onClick={()=>window.print()} className="btn-primary px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-2" data-testid="print-btn">
          <Printer size={16}/> Print / Save PDF
        </button>
      </div>
      <div className="no-print max-w-[210mm] mx-auto mb-3 bg-amber-50 border border-amber-200 text-amber-900 text-xs px-3 py-2 rounded">
        <b>Tip:</b> In the browser print dialog use <b>Layout: Portrait</b>, <b>Paper: A4</b>, <b>Margins: None</b>.
      </div>

      {pods.length > 0 && (
        <div className="no-print max-w-[210mm] mx-auto mb-3 bg-blue-50 border border-blue-200 text-blue-900 text-xs px-3 py-2 rounded" data-testid="bill-pod-section">
          <div className="font-semibold mb-1 inline-flex items-center gap-1"><FileCheck2 size={14}/> Proof of Delivery available for {pods.length} CN(s) — print alongside the bill:</div>
          <div className="flex flex-wrap gap-2 mt-1">
            {pods.map((c) => (
              <button key={c.id} onClick={()=>openPodFile(c.id, true)} className="px-2 py-1 rounded border border-blue-300 bg-white hover:bg-blue-100 inline-flex items-center gap-1" data-testid={`bill-pod-print-${c.cn_no}`}>
                <Printer size={13}/> POD · {c.cn_no}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="bill-print-area bg-white shadow mx-auto" style={{ boxSizing: "border-box" }}>
        <Bill b={b} sig={sig}/>
      </div>
    </div>
  );
}

function Bill({ b, sig }) {
  const nn = (v) => { const x = parseFloat(v); return isNaN(x) ? 0 : x; };
  // Compute totals from items in case stored values are 0/missing
  const itemsTotal = (b.items || []).reduce((s, it) => s + (nn(it.net_amount) || (nn(it.freight)+nn(it.labour)+nn(it.b_exp)+nn(it.lcl))), 0);
  const totalAmount = nn(b.total_amount) || itemsTotal;
  const cgstAmt = totalAmount * nn(b.cgst_percent) / 100;
  const sgstAmt = totalAmount * nn(b.sgst_percent) / 100;
  const igstAmt = totalAmount * nn(b.igst_percent) / 100;
  const taxTotal = cgstAmt + sgstAmt + igstAmt;
  const grand = nn(b.grand_total) || (totalAmount + taxTotal);
  const taxable = nn(b.taxable_amount) || (taxTotal > 0 ? totalAmount : 0);
  const words = b.amount_in_words || rupeesInWords(grand);

  const num = (v) => {
    const x = parseFloat(v);
    if (isNaN(x) || x === 0) return "0";
    return x.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  const transportedList = (b.transported_for || "").split(",").map(s => s.trim()).filter(Boolean);

  return (
    <div style={{ padding: "5mm 8mm 3mm", fontFamily: "'Manrope', system-ui, sans-serif", color: "#111", fontSize: 11, lineHeight: 1.4 }}>
      {/* Header: Logo + Title */}
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <img src={LOGO_URL} alt="JJ" style={{ width: 78, height: 78, objectFit: "contain" }}/>
        <div style={{ flex: 1, borderBottom: "2px solid #2b6bd1", paddingBottom: 4 }}>
          <div style={{ fontFamily: "'Playfair Display', serif", color: "#2b6bd1", fontWeight: 900, fontSize: 28, letterSpacing: 1, lineHeight: 1 }}>JAI JAGDAMBA LOGISTICS</div>
        </div>
      </div>

      {/* FREIGHT - BILL centered with full-width underline */}
      <div style={{ textAlign: "center", marginTop: 8, marginBottom: 0, fontWeight: 700 }}>FREIGHT - BILL</div>
      <div style={{ borderBottom: "1px solid #000", marginTop: 5 }}></div>

      {/* Bill info row - borderless left, boxed right */}
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          <tr>
            <td style={{ padding: "10px 0 14px", verticalAlign: "top", width: "62%" }}>
              <div style={{ fontWeight: 700, textDecoration: "underline", marginBottom: 6 }}>Billed To</div>
              <table style={{ borderCollapse: "collapse" }}>
                <tbody>
                  <tr>
                    <td style={{ fontWeight: 700, verticalAlign: "top", paddingRight: 6, whiteSpace: "nowrap" }}>Party Name</td>
                    <td style={{ verticalAlign: "top", paddingRight: 6 }}>:</td>
                    <td style={{ fontWeight: 800, fontSize: 12, verticalAlign: "top" }}>{b.party_name}</td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 700, verticalAlign: "top", paddingRight: 6, whiteSpace: "nowrap" }}>Party Address</td>
                    <td style={{ verticalAlign: "top", paddingRight: 6 }}>:</td>
                    <td style={{ verticalAlign: "top", whiteSpace: "pre-line" }}>{b.party_address}</td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 700, verticalAlign: "top", paddingRight: 6, whiteSpace: "nowrap" }}>GSTIN</td>
                    <td style={{ verticalAlign: "top", paddingRight: 6 }}>:</td>
                    <td style={{ fontWeight: 700, verticalAlign: "top" }}>{b.party_gstin}</td>
                  </tr>
                </tbody>
              </table>
            </td>
            <td style={{ padding: 0, verticalAlign: "top", width: "38%" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #000" }}>
                <tbody>
                  <tr>
                    <td style={{ padding: "8px 10px", borderBottom: "1px solid #000", width: "35%" }}>Bill NO</td>
                    <td style={{ padding: "8px 10px", borderBottom: "1px solid #000", borderLeft: "1px solid #000", fontWeight: 700 }}>{b.bill_no}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: "8px 10px", borderBottom: "1px solid #000" }}>Bill date</td>
                    <td style={{ padding: "8px 10px", borderBottom: "1px solid #000", borderLeft: "1px solid #000" }}>{fmtDate(b.date)}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: "8px 10px" }}>SAC NO</td>
                    <td style={{ padding: "8px 10px", borderLeft: "1px solid #000" }}>{b.sac_no || "996791"}</td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
          <tr>
            <td colSpan={2} style={{ borderTop: "1px solid #000", padding: "8px 0" }}>
              <b>TPN #</b>&nbsp;&nbsp;&nbsp;<b>{b.tpn_no}</b>
            </td>
          </tr>
        </tbody>
      </table>

      {/* We Hereby text */}
      <div style={{ padding: "14px 4px 10px" }}>We Hereby submit Our freight Bill For Transportation of Goods.</div>

      {/* Line items table */}
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
        <thead>
          <tr>
            <th style={th()}>S.NO</th>
            <th style={th()}>Date</th>
            <th style={th()}>Consignment</th>
            <th style={th()}>Destination</th>
            <th style={th()}>Invoice No</th>
            <th style={th()}>Lorry No</th>
            <th style={th()}>Pkg/Wgt.</th>
            <th style={{ ...th(), textAlign: "right" }}>Freight</th>
            <th style={{ ...th(), textAlign: "right" }}>Labour</th>
            <th style={{ ...th(), textAlign: "right" }}>B.Exp</th>
            <th style={{ ...th(), textAlign: "right" }}>Detain</th>
            <th style={{ ...th(), textAlign: "right" }}>Net Amount</th>
          </tr>
        </thead>
        <tbody>
          {b.items.map((it, i) => (
            <tr key={i}>
              <td style={td()}>{i+1}</td>
              <td style={td()}>{fmtDate(it.date)}</td>
              <td style={tdw()}>{it.cn_no}</td>
              <td style={tdw()}>{it.destination}</td>
              <td style={tdw()}>{it.invoice_no}</td>
              <td style={tdw()}>{it.lorry_no}</td>
              <td style={tdw()}>{it.pkg_wgt}</td>
              <td style={{ ...td(), textAlign: "right" }}>{it.freight}</td>
              <td style={{ ...td(), textAlign: "right" }}>{it.labour}</td>
              <td style={{ ...td(), textAlign: "right" }}>{it.b_exp}</td>
              <td style={{ ...td(), textAlign: "right" }}>{it.lcl}</td>
              <td style={{ ...td(), textAlign: "right" }}>{num(it.net_amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Gap between CN details table and bottom (bank/totals) section */}
      <div style={{ marginTop: 30, marginBottom: 20 }}>
        <div style={{ marginBottom: 6 }}>Transported Goods For:-</div>
        {transportedList.map((t, i) => (
          <div key={i} style={{ paddingLeft: 8, marginTop: 2 }}>{String.fromCharCode(65 + i)}.) {t}</div>
        ))}
        {transportedList.length === 0 && <div style={{ paddingLeft: 8, color: "#888" }}></div>}
      </div>

      {/* Bank details + totals section */}
      <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #000" }}>
        <tbody>
          <tr>
            <td style={{ padding: "6px 8px", verticalAlign: "top", width: "40%" }}>
              <div>Our Bank Details :</div>
              <div style={{ fontWeight: 700 }}>PUNJAB NATIONAL BANK</div>
              <div>A/C - 0556050014173</div>
              <div>IFSC - PUNB0055620</div>
              <div>BRANCH - POSTA , KOLKATA 700006</div>
            </td>
            <td style={{ padding: "6px 8px", verticalAlign: "top", width: "30%", borderLeft: "1px solid #000", textAlign: "right" }}>
              <div>GSTIN NO -: 19AAHPO6792Q1ZJ</div>
              <div>MSME NO. UDYAM-WB-10-0020012</div>
              <div>PAN NO -: AAHPO6792Q</div>
            </td>
            <td style={{ padding: 0, verticalAlign: "top", width: "30%", borderLeft: "1px solid #000" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <tbody>
                  <TotalRow label="Total Amount" value={num(totalAmount)} />
                  <TotalRow label="Taxable Amount" value={num(taxable)} />
                  <TotalRow label={`CGST ${b.cgst_percent}% @`} value={num(cgstAmt)} />
                  <TotalRow label={`SGST ${b.sgst_percent}% @`} value={num(sgstAmt)} />
                  <TotalRow label={`IGST ${b.igst_percent}% @`} value={num(igstAmt)} last />
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>

      {/* Amount in words + Grand total */}
      <table style={{ width: "100%", borderCollapse: "collapse", borderLeft: "1px solid #000", borderRight: "1px solid #000", borderBottom: "1px solid #000" }}>
        <tbody>
          <tr>
            <td style={{ padding: "6px 8px", width: "70%" }}>
              Rupees - <i>{words}</i>
            </td>
            <td style={{ padding: "6px 8px", borderLeft: "1px solid #000", fontWeight: 700 }}>Grand Total Rs.</td>
            <td style={{ padding: "6px 8px", borderLeft: "1px solid #000", textAlign: "right", fontWeight: 700 }}>{num(grand)}</td>
          </tr>
          <tr>
            <td colSpan={3} style={{ padding: "6px 8px", borderTop: "1px solid #000" }}>
              Remark -: <b>{b.remarks}</b>
            </td>
          </tr>
        </tbody>
      </table>

      {/* Note + Terms wrapper (relative) with signature stamped top-aligned to the Note line */}
      <div style={{ position: "relative" }}>
        <div style={{ padding: "8px 0 5px", fontWeight: 700 }}>Note :- GST PAYABLE BY CUSTOMER UNDER REVERSE CHARGE MACHANISUM</div>
        <div style={{ border: "1px solid #000", padding: "6px 8px" }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Terms and condition*</div>
          <div style={{ fontSize: 10.5, lineHeight: 1.35 }}>
            All Payment Should be made by A/C Payee Cheque in favour of Jai Jagdamba Logistics. All Complaints in respect of this bill must be forwarded within 15 days of receipt of the Bill. 2.5% Interest (Per 30 Days for all consecutive 30 Days) interest will be charged on bills not paid within Due Date. If the Due Date is Sundays or National Holidays next Working Day will be considered as Due Date. Jai Jagdamba Logistics reserves the right to take action as per LAW for Instruments returned by your bank or to recover its outstanding. Kindly note that due to unavoidable circumstances, the above stated terms may change, modify, vary, alter or be amended from time to time without any prior notice. Any disputes should they arise would be subject to the exclusive jurisdiction of courts in KOLKATA
          </div>
        </div>
        <div style={{ position: "absolute", right: 20, top: 0, width: 150, textAlign: "center", zIndex: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 10.5 }}>For JAI JAGDAMBA LOGISTICS</div>
          {sig && sig.has_signature && (
            <img src={`${API_BASE}/auth/users/${sig.user_id}/signature`} alt="Signature" style={{ width: 150, maxHeight: 120, objectFit: "contain", mixBlendMode: "multiply", background: "transparent", WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }} data-testid="bill-signature-img"/>
          )}
          <div style={{ fontSize: 10 }}>Authorised Signatory</div>
        </div>
      </div>

      {/* Page indicator */}
      <div style={{ paddingTop: 3, paddingBottom: 1 }}>Page 1 Of 1</div>

      {/* Footer - tight spacing */}
      <div style={{ borderTop: "1px solid #000", paddingTop: 2, marginTop: 1, textAlign: "center", fontSize: 10.5, lineHeight: 1.3 }}>
        <div>17/1, Lakshmi Narayan Mukherjee Lane, Room No.-209, 2nd Floor, Kolkata - 700006</div>
        <div>E-Mail : jjlogistics.india@gmail.com | Contact : 9831997959 / 9311537613 / 9664273752</div>
      </div>
    </div>
  );
}

function th() { return { border: "1px solid #000", padding: "5px 6px", textAlign: "left", background: "#f5f5f5", fontWeight: 700, whiteSpace: "nowrap" }; }
function td() { return { border: "1px solid #000", padding: "5px 6px", verticalAlign: "top", whiteSpace: "nowrap" }; }
// Wrapping cell for long free-text columns: wraps to a new line but never breaks mid-token.
function tdw() { return { border: "1px solid #000", padding: "5px 6px", verticalAlign: "top", whiteSpace: "normal", overflowWrap: "break-word", wordBreak: "break-word", maxWidth: "26mm" }; }

function rupeesInWords(num) {
  const a = ["","One","Two","Three","Four","Five","Six","Seven","Eight","Nine","Ten","Eleven","Twelve","Thirteen","Fourteen","Fifteen","Sixteen","Seventeen","Eighteen","Nineteen"];
  const b = ["","","Twenty","Thirty","Fourty","Fifty","Sixty","Seventy","Eighty","Ninety"];
  const inr = (x)=> {
    if (x < 20) return a[x];
    if (x < 100) return b[Math.floor(x/10)] + (x%10 ? " " + a[x%10] : "");
    if (x < 1000) return a[Math.floor(x/100)] + " Hundred" + (x%100 ? " " + inr(x%100) : "");
    if (x < 100000) return inr(Math.floor(x/1000)) + " Thousand" + (x%1000 ? " " + inr(x%1000) : "");
    if (x < 10000000) return inr(Math.floor(x/100000)) + " Lakh" + (x%100000 ? " " + inr(x%100000) : "");
    return inr(Math.floor(x/10000000)) + " Crore" + (x%10000000 ? " " + inr(x%10000000) : "");
  };
  const whole = Math.floor(num);
  return whole ? inr(whole) + " rupees only." : "Zero rupees only.";
}

function TotalRow({ label, value, last }) {
  const b = last ? {} : { borderBottom: "1px solid #000" };
  return (
    <tr>
      <td style={{ padding: "4px 8px", ...b }}>{label}</td>
      <td style={{ padding: "4px 8px", borderLeft: "1px solid #000", textAlign: "right", ...b }}>{value}</td>
    </tr>
  );
}
